import { useEffect, useState } from 'react';
import { Dialog } from 'sharedUi/Dialog';
import AiFeedbackBar from '../../components/ai-feedback/ai-feedback-bar';
import { conversationsService } from '../../services/conversations.service';
import type { ConversationMessage as Msg, ConversationSource, SourcePassage } from '../../types/conversation.types';

interface Props {
  message: Msg;
  conversationId: string;
  agentCode: string;
  showSources?: boolean;
  showFeedback?: boolean;
  assistantIcon?: string;
  accentColor?: string;
}

const isTemp = (id: string) => id.startsWith('temp-');

/** One message bubble: user on the right, assistant on the left. */
const ConversationMessage = ({ message, conversationId, agentCode, showSources = true, showFeedback = true,
  assistantIcon = 'pi pi-android', accentColor }: Props) => {
  const accentStyle = accentColor ? { backgroundColor: `${accentColor}1a`, color: accentColor } : undefined;
  const isUser = message.role === 'user';
  const streaming = !isUser && message.status === 'PENDING';
  const failed = message.status === 'FAILED';
  const model = message.model?.modelId;
  const meta = [model, message.latencyMs ? `${message.latencyMs}ms` : null].filter(Boolean).join(' · ');

  // The answer's [n] markers point at `citations`; `sources` is everything retrieved.
  // Default to what was actually cited; let the reader expand to all.
  const cited = message.retrieval?.citations || [];
  const retrieved = message.retrieval?.sources || [];
  const [showAll, setShowAll] = useState(false);
  const [detail, setDetail] = useState<ConversationSource | null>(null);
  const [passages, setPassages] = useState<SourcePassage[] | null>(null);
  const [loadingPassages, setLoadingPassages] = useState(false);
  const shown = showAll ? retrieved : (cited.length ? cited : retrieved);

  // On opening a source, fetch its passage text from the index (not in the payload).
  useEffect(() => {
    if (!detail || detail.index == null || isTemp(message._id)) { setPassages(null); return; }
    let active = true;
    setLoadingPassages(true);
    setPassages(null);
    conversationsService.getMessageSource(conversationId, message._id, Number(detail.index))
      .then((d) => { if (active) setPassages(d.passages || []); })
      .catch(() => { if (active) setPassages([]); })
      .finally(() => { if (active) setLoadingPassages(false); });
    return () => { active = false; };
  }, [detail, conversationId, message._id]);

  return (
    <div className={`flex items-start gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
        isUser ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300' : 'bg-primary/10 text-primary'
      }`} style={isUser ? undefined : accentStyle} aria-hidden>
        <i className={`${isUser ? 'pi pi-user' : assistantIcon} text-sm`} />
      </div>

      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
        isUser
          ? 'bg-primary text-primary-foreground'
          : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
      }`}>
        {streaming && !message.content ? (
          <span className="inline-flex gap-1 py-1" aria-label="Assistant is typing">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400" />
          </span>
        ) : (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        )}

        {failed && message.error && (
          <div className="mt-1.5 text-xs text-red-600 dark:text-red-400">
            {message.error.message} <span className="font-mono opacity-70">({message.error.code})</span>
          </div>
        )}

        {!isUser && showSources && retrieved.length > 0 && (
          <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] font-medium text-ink-muted">
                {showAll ? 'All retrieved' : 'Sources'}
              </span>
              {cited.length > 0 && cited.length !== retrieved.length && (
                <button type="button" className="text-[11px] text-primary hover:underline"
                  onClick={() => setShowAll((v) => !v)}>
                  {showAll ? `Show cited only (${cited.length})` : `Show all retrieved (${retrieved.length})`}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {shown.map((s, i) => {
                const n = s.index ?? i + 1;
                const name = s.source || 'source';
                return (
                  <button key={s.sourceRef || s.index || i} type="button"
                    onClick={() => setDetail(s)}
                    title={`${name}${s.page ? ` · p.${s.page}` : ''} — click for details`}
                    className="ui-press max-w-[14rem] truncate rounded-md bg-white px-1.5 py-0.5 text-[11px] text-gray-600 shadow-sm hover:ring-1 hover:ring-primary/40 dark:bg-gray-900 dark:text-gray-300">
                    <span className="font-medium text-primary">[{n}]</span> {name}{s.page ? ` · p.${s.page}` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Feedback only on a real, finished answer — never a streaming placeholder. */}
        {!isUser && showFeedback && message.status === 'COMPLETED' && !isTemp(message._id) && (
          <div className="mt-1.5">
            <AiFeedbackBar
              componentType="AI_MESSAGE"
              componentCode={agentCode}
              componentId={message._id}
              chatSessionId={conversationId}
              messageId={message._id}
              traceId={message.observabilityId || null}
              meta={meta || undefined}
            />
          </div>
        )}
      </div>

      {/* Source detail — the chat payload carries source metadata; the passage text
          is fetched on demand from the index. */}
      <Dialog visible={!!detail} onHide={() => setDetail(null)} header="Source"
        style={{ width: '560px', maxWidth: '95vw' }}>
        {detail && (
          <div className="flex flex-col gap-4">
            {/* Document + meta pills */}
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-ink-muted">Document</div>
              <div className="break-words text-base font-semibold text-gray-900 dark:text-gray-100">{detail.source || '—'}</div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">Citation [{detail.index}]</span>
                {detail.page != null && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-300">Page {detail.page}</span>
                )}
                {detail.score != null && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600 dark:bg-gray-800 dark:text-gray-300">Relevance {Number(detail.score).toFixed(3)}</span>
                )}
              </div>
            </div>

            {message.retrieval?.standaloneQuery && (
              <div className="text-xs text-ink-muted">
                Retrieved for <span className="italic text-gray-600 dark:text-gray-300">&ldquo;{message.retrieval.standaloneQuery}&rdquo;</span>
              </div>
            )}

            {/* Passage text */}
            <div>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-muted">
                {(passages?.length ?? 0) > 1 ? `Passages (${passages?.length})` : 'Passage'}
              </div>
              {loadingPassages ? (
                <p className="text-xs text-ink-muted">Loading passage…</p>
              ) : passages && passages.length > 0 ? (
                <div className="flex max-h-80 flex-col gap-2 overflow-y-auto pr-1">
                  {passages.map((p, i) => (
                    <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                      {p.page != null && (
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted">Page {p.page}</div>
                      )}
                      <p className="m-0 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-gray-800 dark:text-gray-200">
                        {p.content || '(empty)'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">No passage text available for this source.</p>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ConversationMessage;
